/**
 * Authentication Utility
 *
 * Admin routes: JWT tokens in HttpOnly cookies (auto-sent by browser)
 * External API routes: OAuth2 Client Credentials tokens (in-memory)
 */

// Admin user info key (sessionStorage - non-sensitive display data only)
const ADMIN_USER_KEY = 'admin_user';
const SESSION_ID_KEY = 'partsmart_session_id';

// In-memory OAuth2 token cache (not persisted to storage for security)
let _oauth2AccessToken: string | null = null;
let _oauth2TokenExpiry: number = 0;
let _oauth2TokenPromise: Promise<string | null> | null = null;

// ============================================================================
// Session Management
// ============================================================================

/**
 * Get or create a session ID for per-visitor analytics tracking.
 * Stored in sessionStorage (cleared on browser close).
 */
export const getOrCreateSessionId = (): string => {
  try {
    let sessionId = sessionStorage.getItem(SESSION_ID_KEY);
    if (!sessionId) {
      sessionId = `sess_${Date.now()}_${Math.random().toString(36).substring(2, 10)}`;
      sessionStorage.setItem(SESSION_ID_KEY, sessionId);
    }
    return sessionId;
  } catch {
    return `sess_${Date.now()}_${Math.random().toString(36).substring(2, 10)}`;
  }
};

// ============================================================================
// Admin Authentication (HttpOnly Cookie-based)
// ============================================================================

/**
 * Get CSRF token from the non-HttpOnly csrf_token cookie.
 */
const getCsrfToken = (): string | null => {
  const match = document.cookie.match(/(?:^|;\s*)csrf_token=([^;]+)/);
  return match ? match[1] : null;
};

/**
 * Get admin user info from sessionStorage (non-sensitive display data).
 */
export const getAdminUser = (): any | null => {
  try {
    const userStr = sessionStorage.getItem(ADMIN_USER_KEY);
    if (!userStr) return null;
    return JSON.parse(userStr);
  } catch (error) {
    console.error('Failed to get admin user:', error);
    return null;
  }
};

/**
 * Clear admin session data (logout).
 * HttpOnly cookies are cleared server-side via the logout endpoint.
 */
export const clearAdminTokens = (): void => {
  try {
    sessionStorage.removeItem(ADMIN_USER_KEY);
  } catch (error) {
    console.error('Failed to clear admin data:', error);
  }
};

/**
 * Refresh admin access token via HttpOnly cookie.
 * The refresh token cookie is sent automatically by the browser.
 */
export const refreshAdminToken = async (): Promise<boolean> => {
  try {
    const backendUrl = getBackendUrlNormalized();

    const response = await fetch(`${backendUrl}/api/auth/admin/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
    });

    if (!response.ok) {
      throw new Error('Token refresh failed');
    }

    return true;
  } catch (error) {
    console.error('Token refresh failed:', error);
    clearAdminTokens();
    return false;
  }
};

/**
 * Check if user is admin (has admin_user in sessionStorage).
 * The actual auth token is in an HttpOnly cookie (JS cannot read it).
 */
export const isAdminAuthenticated = (): boolean => {
  return getAdminUser() !== null;
};

/**
 * @deprecated Admin auth now uses HttpOnly cookies. Use adminFetch() instead.
 * Kept for backward compatibility - returns null since tokens are in HttpOnly cookies.
 */
export const getAdminToken = (): string | null => {
  // Token is in HttpOnly cookie, JS cannot read it.
  // Return null; admin pages should use adminFetch() which sends cookies automatically.
  return isAdminAuthenticated() ? '__cookie__' : null;
};

/**
 * @deprecated Admin auth now uses HttpOnly cookies. Use adminFetch() instead.
 * Returns null since admin tokens are in HttpOnly cookies (auto-sent by browser).
 */
export const getAuthHeader = (): string | null => {
  return null;
};

/**
 * Fetch wrapper for admin API calls.
 * - Sends HttpOnly auth cookies automatically (credentials: 'include')
 * - Adds CSRF token header for state-changing methods (POST/PUT/DELETE/PATCH)
 */
export const adminFetch = async (url: string, options: RequestInit = {}): Promise<Response> => {
  const headers = new Headers(options.headers);

  // Add CSRF token for state-changing methods
  const method = (options.method || 'GET').toUpperCase();
  if (['POST', 'PUT', 'DELETE', 'PATCH'].includes(method)) {
    const csrfToken = getCsrfToken();
    if (csrfToken) {
      headers.set('X-CSRF-Token', csrfToken);
    }
  }

  // Set Content-Type if not already set and not FormData
  if (!headers.has('Content-Type') && !(options.body instanceof FormData)) {
    headers.set('Content-Type', 'application/json');
  }

  return fetch(url, {
    ...options,
    headers,
    credentials: 'include',
  });
};

// ============================================================================
// OAuth2 Client Credentials Authentication
// ============================================================================

/**
 * Get the backend URL with IPv4 normalization.
 */
const getBackendUrlNormalized = (): string => {
  let backendUrl = process.env.REACT_APP_BACKEND_URL || '';
  try {
    const urlObj = new URL(backendUrl);
    if (urlObj.hostname === 'localhost' || urlObj.hostname === '::1') {
      urlObj.hostname = '127.0.0.1';
      backendUrl = urlObj.toString().replace(/\/$/, '');
    }
  } catch (e) {
    backendUrl = backendUrl.replace(/localhost/g, '127.0.0.1').replace(/::1/g, '127.0.0.1');
  }
  return backendUrl;
};

/**
 * Get OAuth2 client credentials from environment variables.
 */
const getOAuth2Credentials = (): { clientId: string; clientSecret: string } => {
  return {
    clientId: process.env.REACT_APP_OAUTH_CLIENT_ID || '',
    clientSecret: process.env.REACT_APP_OAUTH_CLIENT_SECRET || '',
  };
};

/**
 * Check if OAuth2 credentials are configured.
 */
const isOAuth2Configured = (): boolean => {
  const { clientId, clientSecret } = getOAuth2Credentials();
  return !!(clientId && clientSecret);
};

/**
 * Fetch a new OAuth2 access token using client credentials grant.
 */
const fetchOAuth2Token = async (): Promise<string | null> => {
  const { clientId, clientSecret } = getOAuth2Credentials();
  console.log('[OAuth2] Fetching token with clientId:', clientId ? clientId.substring(0, 10) + '...' : 'MISSING');

  if (!clientId || !clientSecret) {
    console.error('[OAuth2] Missing credentials - clientId:', !!clientId, 'clientSecret:', !!clientSecret);
    return null;
  }

  try {
    const backendUrl = getBackendUrlNormalized();
    console.log('[OAuth2] Backend URL:', backendUrl);

    const body = new URLSearchParams({
      grant_type: 'client_credentials',
      client_id: clientId,
      client_secret: clientSecret,
    });

    const response = await fetch(`${backendUrl}/api/auth/oauth/token`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: body.toString(),
    });

    console.log('[OAuth2] Response status:', response.status);

    if (!response.ok) {
      const errorText = await response.text();
      console.error('[OAuth2] Token request failed:', response.status, errorText);
      return null;
    }

    const data = await response.json();
    if (data.access_token) {
      _oauth2AccessToken = data.access_token;
      // Set expiry with 5-minute buffer for early refresh
      _oauth2TokenExpiry = Date.now() + ((data.expires_in - 300) * 1000);
      console.log('[OAuth2] Token obtained successfully, expires in:', data.expires_in, 'seconds');
      return data.access_token;
    }

    console.error('[OAuth2] No access_token in response:', data);
    return null;
  } catch (error) {
    console.error('[OAuth2] Token fetch error:', error);
    return null;
  }
};

/**
 * Get a valid OAuth2 access token, refreshing if needed.
 * Uses a singleton promise to prevent concurrent token requests.
 */
export const getOAuth2Token = async (): Promise<string | null> => {
  // Return cached token if still valid
  if (_oauth2AccessToken && Date.now() < _oauth2TokenExpiry) {
    return _oauth2AccessToken;
  }

  // Prevent concurrent token requests
  if (_oauth2TokenPromise) {
    return _oauth2TokenPromise;
  }

  _oauth2TokenPromise = fetchOAuth2Token().finally(() => {
    _oauth2TokenPromise = null;
  });

  return _oauth2TokenPromise;
};

/**
 * Ensure OAuth2 token is loaded. Call this during app initialization.
 */
export const ensureOAuth2Token = async (): Promise<void> => {
  if (isOAuth2Configured()) {
    await getOAuth2Token();
  }
};

/**
 * Clear the OAuth2 token cache.
 */
export const clearOAuth2Token = (): void => {
  _oauth2AccessToken = null;
  _oauth2TokenExpiry = 0;
};

// ============================================================================
// API Headers (for non-admin API calls)
// ============================================================================

/**
 * Get headers for non-admin API calls using OAuth2 Bearer token.
 * Note: This is synchronous and uses cached token. For guaranteed auth, use getApiKeyHeadersAsync.
 */
export const getApiKeyHeaders = (): HeadersInit => {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    'X-Session-Id': getOrCreateSessionId(),
  };

  if (_oauth2AccessToken && Date.now() < _oauth2TokenExpiry) {
    headers['Authorization'] = `Bearer ${_oauth2AccessToken}`;
  }

  return headers;
};

/**
 * Get headers for non-admin API calls, ensuring OAuth2 token is fetched first.
 * Use this version for API calls that need guaranteed authentication.
 */
export const getApiKeyHeadersAsync = async (): Promise<HeadersInit> => {
  console.log('[Auth] getApiKeyHeadersAsync called');
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    'X-Session-Id': getOrCreateSessionId(),
  };

  // Ensure token is fetched if OAuth2 is configured
  if (isOAuth2Configured()) {
    console.log('[Auth] OAuth2 is configured, fetching token...');
    const token = await getOAuth2Token();
    console.log('[Auth] Token result:', token ? 'obtained' : 'FAILED');
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
  }

  return headers;
};

/**
 * Get headers for non-admin API calls that include file uploads (no Content-Type).
 * Browser will set the correct Content-Type with boundary for FormData.
 */
export const getApiKeyHeadersForUpload = (): HeadersInit => {
  const headers: Record<string, string> = {
    'X-Session-Id': getOrCreateSessionId(),
  };

  if (_oauth2AccessToken && Date.now() < _oauth2TokenExpiry) {
    headers['Authorization'] = `Bearer ${_oauth2AccessToken}`;
  }

  return headers;
};

/**
 * Get headers for file uploads, ensuring OAuth2 token is fetched first.
 */
export const getApiKeyHeadersForUploadAsync = async (): Promise<HeadersInit> => {
  const headers: Record<string, string> = {
    'X-Session-Id': getOrCreateSessionId(),
  };

  if (isOAuth2Configured()) {
    const token = await getOAuth2Token();
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
  }

  return headers;
};

// ============================================================================
// Legacy exports (kept for backward compatibility, will be removed)
// ============================================================================

export const getToken = (): string | null => null;
export const isTokenExpired = (): boolean => true;
export const clearTokens = (): void => {};
export const isAuthenticated = (): boolean => {
  return !!(_oauth2AccessToken && Date.now() < _oauth2TokenExpiry);
};
